import { Loadable } from '@/components/loaders';
import { setPagination } from '@/features/dataSources/dataSourcesSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { FrontTable, TablePager } from '@/lib';
import { DataSource } from '@/models/dataSource';
import { RootState } from '@/reducers';
import { useGetDataSourceCountQuery, useLazyGetDataSourcesQuery } from '@/services/dataSources';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getDataSourcesTableColumns } from './helper';

export const DataSourcesTable: FC = () => {
  const { t } = useTranslation();
  const workspace = useWorkspace();

  const [getDataSources, { data, isLoading, isFetching }] = useLazyGetDataSourcesQuery();
  const [dataSources, setDataSources] = useState<DataSource[]>([]);

  const searchText = useSelector((state: RootState) => state.dataSources.ui.searchText);
  const validSearchText = searchText && searchText.length > 2 ? searchText : undefined;

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetDataSourceCountQuery({
    workspaceId: workspace.id,
    searchText: validSearchText,
  });

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) =>
      getDataSources({
        workspaceId: workspace.id,
        offset: pageNo * pageSize,
        limit: pageSize,
        searchText: validSearchText,
      }),
    [validSearchText]
  );

  const columns = useMemo(() => getDataSourcesTableColumns(t), [t]);

  return (
    <TablePager
      stateSelector={(state) => state.dataSources.ui.pagination}
      stateUpdater={setPagination}
      query={queryCallback}
      queryResponse={data ? data.items : undefined}
      setItems={setDataSources}
      totalCount={totalCount}
      querying={isTotalCountFetching || isFetching}
      wrapperClassNames='mb-0'
    >
      {(pager) => (
        <Loadable isLoading={isLoading}>
          <FrontTable tableData={dataSources} columns={columns} footer={pager} />
        </Loadable>
      )}
    </TablePager>
  );
};
